import { Action } from 'components/pages/task/action'
import { AppUserValue } from 'lib/actions/apps/app-users'
import { CancelFunc, emptyCancel } from 'lib/cancel'
import { NotifyClient } from 'lib/notify'
import { ActionReturn, GetReturn, ListReturn, Status } from 'lib/rpc'
import { actionInvoke, ActionStatus, Collector, expandValue, expandValueList, unaryInvoke, unaryListCollectSinglePage } from 'lib/rpcts'
import { AccessReview } from 'pbts/c1/api/accessreview/v1/accessreview'
import { App } from 'pbts/c1/api/app/v1/app'
import { AppEntitlement } from 'pbts/c1/api/app/v1/app_entitlement'
import {
    TaskActionsServiceApproveRequest,
    TaskActionsServiceApproveResponse,
    TaskActionsServiceCloseRequest,
    TaskActionsServiceCloseResponse,
    TaskActionsServiceCommentRequest,
    TaskActionsServiceCommentResponse,
    TaskActionsServiceDenyRequest,
    TaskActionsServiceDenyResponse,
    TaskActionsServiceEscalateToEmergencyAccessRequest,
    TaskActionsServiceGetBulkActionProgressResponse,
    TaskActionsServiceListBulkActionInProgressResponse,
    TaskActionsServiceMarkProvisionCompleteRequest,
    TaskActionsServiceMarkProvisionErroredRequest,
    TaskActionsServiceReassignRequest,
    TaskActionsServiceRestartRequest,
    TaskActionsServiceRestartResponse,
    TaskServiceActionResponse,
} from 'pbts/c1/api/task/v1/actions'
import { TaskActionsServiceClient } from 'pbts/c1/api/task/v1/actions.client'
import { Insight } from 'pbts/c1/api/task/v1/insights'
import {
    GetAllAppsForTaskSearchRequest,
    GetAllAppsForTaskSearchResponse,
    TaskSearchCountRequest,
    TaskSearchCountResponse,
    TaskSearchGetProgressRequest,
    TaskSearchGetProgressResponse,
    TaskSearchRequest,
    TaskSearchResponse,
} from 'pbts/c1/api/task/v1/search'
import { TaskSearchServiceClient } from 'pbts/c1/api/task/v1/search.client'
import {
    Task,
    TaskActionType,
    TaskApprovers,
    TaskServiceCreateRequest,
    TaskServiceCreateRevokeRequest,
    TaskServiceCreateRevokeResponse,
    TaskServiceDeleteRequest,
    TaskServiceDeleteResponse,
    TaskServiceGetResponse,
    TaskServiceGetSendReminderPreviewRequest,
    TaskServiceGetSendReminderPreviewResponse,
    TaskServiceListResponse,
    TaskServiceSendReminderRequest,
    TaskType,
    TaskView,
} from 'pbts/c1/api/task/v1/task'
import { TaskServiceClient } from 'pbts/c1/api/task/v1/task.client'
import { User } from 'pbts/c1/api/user/v1/user'
import { Any } from 'pbts/google/protobuf/any'
import { Empty } from 'pbts/google/protobuf/empty'
import { Code as pbCode } from 'pbts/google/rpc/code'
import { Status as pbStatus } from 'pbts/google/rpc/status'
import { Dispatch } from 'react'
import { expandInsights, expandTaskAppUser } from 'redux_toolkit/tasks'

export type TaskStatuses =
    | 'approved'
    | 'closed'
    | 'commented'
    | 'deleted'
    | 'denied'
    | 'error'
    | 'processing'
    | 'reassigned'
    | 'restart'
    | 'provisioned'
    | 'escalateToEmergencyAccess'
    | undefined

export interface TaskValue extends Task {
    user?: User
    createdByUser?: User
    appEntitlements?: AppEntitlement[]
    // TODO: Turn this into an AppView when we do an expand objects pass of the app service
    app?: App
    appUser?: AppUserValue
    accessReview?: AccessReview
    status?: TaskStatuses
    stepApprovers?: User[]
    approvers?: User[]
    identityUser?: User
    ticketActionId?: string
    insights?: Insight[]
    stepUpRedirectUrl?: string
}

export interface TaskViewResponse {
    taskView?: TaskView
    expanded: Any[]
}

export const selectBulkActionsInProgressForAccessReview = (
    payload: ActionStatus<TaskActionsServiceListBulkActionInProgressResponse>,
    accessReviewId: string,
): ActionStatus<TaskActionsServiceListBulkActionInProgressResponse> => {
    switch (payload.status) {
        case Status.OK:
            return {
                status: Status.OK,
                item: {
                    list: payload.item.list.filter((p) => p.searchRequest?.accessReviewIds.includes(accessReviewId)),
                },
            }
        default:
            return payload
    }
}

export const expandTaskPayload = (payload: ActionStatus<TaskViewResponse>): ActionStatus<TaskValue> => {
    switch (payload.status) {
        case Status.OK:
            return {
                status: Status.OK,
                item: expandTaskView(payload.item.taskView, payload.item.expanded),
            }
        default:
            return payload
    }
}

export class TaskCollector extends Collector<TaskValue> {
    push(resp: TaskServiceListResponse): void {
        this.buf = this.buf.concat(
            resp.list.map((v): TaskValue => {
                return expandTaskView(v, resp.expanded)
            }),
        )
    }
}

export const expandTaskView = (msg: TaskView | undefined, values: Any[]): TaskValue => {
    let app = expandValue<App>(msg?.appPath || '', App, values)
    if (app === undefined) {
        const appErr = expandValue<pbStatus>(msg?.appPath || '', pbStatus, values)
        if (appErr?.code === pbCode.NOT_FOUND) {
            app = App.create({
                displayName: 'Deleted App',
            })
        }
    }

    // need to do this for task tables not using redux yet
    const expandedInsights = expandInsights(expandValueList<Insight>(msg?.insightsPath || '', Insight, values))

    return {
        ...Task.create(msg?.task ?? {}),
        accessReview: expandValue<AccessReview>(msg?.accessReviewPath || '', AccessReview, values),
        user: expandValue<User>(msg?.userPath || '', User, values),
        appUser: expandTaskAppUser(msg, values),
        createdByUser: expandValue<User>(msg?.createdByUserPath || '', User, values),
        appEntitlements: expandValueList<AppEntitlement>(msg?.entitlementsPath || '', AppEntitlement, values),
        stepApprovers: expandValueList<User>(msg?.stepApproversPath || '', User, values),
        approvers: expandValueList<User>(msg?.approversPath || '', User, values),
        app,
        status: undefined,
        identityUser: expandValue<User>(msg?.identityUserPath || '', User, values),
        insights: expandedInsights,
    }
}

export enum TasksAction {
    create = 'TasksAction.create',
    createRevokeTask = 'TaskActions.createRevokeTask',
    createGrantTask = 'TaskActions.createGrantTask',
    list = 'TasksAction.list',
    searchCount = 'TasksAction.searchCount',
    delete = 'TasksAction.delete',
    approve = 'TasksAction.approve',
    deny = 'TasksAction.deny',
    close = 'TasksAction.close',
    comment = 'TasksAction.comment',
    provisionComplete = 'TasksAction.provisionComplete',
    provisionErrored = 'TasksAction.provisionErrored',
    reassign = 'TasksAction.reassign',
    restart = 'TasksAction.restart',
    // eslint-disable-next-line @typescript-eslint/no-shadow
    process = 'TasksAction.process',
    searchForApps = 'TasksAction.searchForApps',
    appRollup = 'TasksAction.appRollup',
    // send reminder
    getSendReminderPreview = 'TasksAction.getSendReminderPreview',
    sendReminder = 'TasksAction.sendReminder',
    bulkActionReassign = 'TasksAction.bulkActionReassign',
    bulkActionApprove = 'TasksAction.bulkActionApprove',
    bulkActionProgress = 'TasksAction.bulkActionProgress',
    listBulkActionProgress = 'TasksAction.listBulkActionProgress',
    getProgress = 'TasksAction.getProgress',
    disableBulkActionButton = 'TasksAction.disableBulkActionButton',
    escalateToEmergencyAccess = 'TasksAction.escalateToEmergencyAccess',
}

export type CreateAction = {
    type: TasksAction.create
    payload: ActionStatus<TaskValue>
}

export type CreateRevokeTaskAction = {
    type: TasksAction.createRevokeTask
    payload: ActionStatus<TaskValue>
}

export type CreateGrantTaskAction = {
    type: TasksAction.createGrantTask
    payload: ActionStatus<TaskValue>
}

export type ListAction = {
    type: TasksAction.list
    payload: ListReturn<TaskValue>
}

export type EscalateToEmergencyAccessAction = {
    type: TasksAction.escalateToEmergencyAccess
    payload: ActionStatus<TaskValue>
}

export type SearchCountAction = {
    type: TasksAction.searchCount
    payload: ActionStatus<TaskSearchCountResponse>
}

interface taskIdProp {
    taskId: string
}

export type DeleteAction = {
    type: TasksAction.delete
    payload: GetReturn<taskIdProp>
}

export type ApproveAction = {
    type: TasksAction.approve
    payload: ActionStatus<TaskValue>
}

export type ProvisionCompleteAction = {
    type: TasksAction.provisionComplete
    payload: ActionStatus<TaskValue>
}

export type ProvisionErroredAction = {
    type: TasksAction.provisionErrored
    payload: ActionStatus<TaskValue>
}

export type DenyAction = {
    type: TasksAction.deny
    payload: ActionStatus<TaskValue>
}

export type CloseAction = {
    type: TasksAction.close
    payload: ActionStatus<TaskValue>
}

export type ReassignAction = {
    type: TasksAction.reassign
    payload: ActionStatus<TaskValue>
}

export type RestartAction = {
    type: TasksAction.restart
    payload: ActionStatus<TaskValue>
}

export type CommentAction = {
    type: TasksAction.comment
    payload: ActionStatus<TaskValue>
}

export type ProcessAction = {
    type: TasksAction.process
    payload: ActionStatus<TaskValue>
}

export type SearchForApps = {
    type: TasksAction.searchForApps
    payload: ActionStatus<App[]>
}

export type GetSendReminderPreview = {
    type: TasksAction.getSendReminderPreview
    payload: ActionStatus<TaskApprovers>
}

export type SendReminder = {
    type: TasksAction.sendReminder
    payload: ActionReturn
}

export type BulkActionReassign = {
    type: TasksAction.bulkActionReassign
    payload: ActionReturn
}

export type BulkActionApprove = {
    type: TasksAction.bulkActionApprove
    payload: ActionReturn
}

export type BulkActionProgress = {
    type: TasksAction.bulkActionProgress
    payload: ActionStatus<TaskActionsServiceGetBulkActionProgressResponse>
}

export type ListBulkActionProgress = {
    type: TasksAction.listBulkActionProgress
    payload: ActionStatus<TaskActionsServiceListBulkActionInProgressResponse>
}

export type GetProgress = {
    type: TasksAction.getProgress
    payload: ActionStatus<TaskSearchGetProgressResponse>
}

export type DisableBulkActionButton = {
    type: TasksAction.disableBulkActionButton
    payload: boolean
}

export type TasksActions =
    | CreateAction
    | CreateRevokeTaskAction
    | CreateGrantTaskAction
    | ListAction
    | DeleteAction
    | ApproveAction
    | DenyAction
    | CloseAction
    | CommentAction
    | ReassignAction
    | ProcessAction
    | RestartAction
    | SearchForApps
    | GetSendReminderPreview
    | SendReminder
    | ProvisionCompleteAction
    | EscalateToEmergencyAccessAction
    | ProvisionErroredAction
    | BulkActionReassign
    | SearchCountAction
    | BulkActionApprove
    | BulkActionProgress
    | ListBulkActionProgress
    | GetProgress
    | DisableBulkActionButton

export type CreateTaskHandlerValues = {
    appID: string
    appEntitlementID: string
    type: TaskType
    description: string
    userID: string
    emergencyAccess: boolean
}

export type CreateRevokeTaskHandlerValues = {
    appID: string
    appEntitlementID: string
    description: string
    appUserId: string
}

export const create = (
    client: TaskServiceClient,
    dispatch: Dispatch<TasksActions>,
    values: CreateTaskHandlerValues,
): CancelFunc => {
    const req = TaskServiceCreateRequest.create({
        description: values.description,
        typ: values.type,
        appId: values.appID,
        userId: values.userID,
        appEntitlementId: values.appEntitlementID,
        emergencyAccess: values.emergencyAccess,
        expandMask: { paths: ['*'] },
    })

    return unaryInvoke<TaskServiceCreateRequest, TaskServiceGetResponse>(req, client.create.bind(client), (payload) => {
        dispatch({
            type: TasksAction.create,
            payload: expandTaskPayload(payload),
        })
    })
}

export const createRevokeTask = (
    client: TaskServiceClient,
    dispatch: Dispatch<TasksActions>,
    values: CreateRevokeTaskHandlerValues,
): CancelFunc => {
    const req = TaskServiceCreateRevokeRequest.create({
        description: values.description,
        appId: values.appID,
        appUserId: values.appUserId,
        appEntitlementId: values.appEntitlementID,
        expandMask: { paths: ['*'] },
    })

    return unaryInvoke<TaskServiceCreateRevokeRequest, TaskServiceCreateRevokeResponse>(
        req,
        client.createRevokeTask.bind(client),
        (payload) => {
            dispatch({
                type: TasksAction.createRevokeTask,
                payload: expandTaskPayload(payload),
            })
        },
    )
}

export const searchTasks = (
    dispatch: Dispatch<TasksActions>,
    client: TaskSearchServiceClient | undefined,
    request?: TaskSearchRequest,
    notifyClient?: NotifyClient,
): CancelFunc => {
    if (!client || !request) {
        return emptyCancel
    }
    request.expandMask = { paths: ['*'] }
    const collector = new TaskCollector()
    return unaryListCollectSinglePage<TaskSearchRequest, TaskSearchResponse>(
        request,
        client.search.bind(client),
        collector,
        (payload) => {
            switch (payload.status) {
                case Status.OK:
                    dispatch({
                        type: TasksAction.list,
                        payload: {
                            status: Status.OK,
                            items: collector.buffer(),
                            metadata: payload.metadata,
                        },
                    })
                    break
                default:
                    dispatch({
                        type: TasksAction.list,
                        payload,
                    })
            }
        },
        notifyClient,
    )
}

export const getSearchTasksCount = (
    dispatch: Dispatch<TasksActions>,
    client: TaskSearchServiceClient | undefined,
    filter?: TaskSearchRequest,
    notifyClient?: NotifyClient,
): CancelFunc => {
    if (!client || !filter) {
        return emptyCancel
    }
    return unaryInvoke<TaskSearchCountRequest, TaskSearchCountResponse>(
        TaskSearchCountRequest.create({
            filter,
        }),
        client.searchCount.bind(client),
        (payload) => {
            switch (payload.status) {
                case Status.OK:
                    dispatch({
                        type: TasksAction.searchCount,
                        payload: {
                            status: Status.OK,
                            item: payload.item,
                        },
                    })
                    break
                default:
                    dispatch({
                        type: TasksAction.searchCount,
                        payload,
                    })
            }
        },
        notifyClient,
    )
}

export const getProgress = (
    dispatch: Dispatch<TasksActions>,
    client: TaskSearchServiceClient | undefined,
    userId: string,
    accessReviewId: string,
    notifyClient?: NotifyClient,
): CancelFunc => {
    if (!client) {
        return emptyCancel
    }
    const req = TaskSearchGetProgressRequest.create({
        userId,
        accessReviewId,
    })

    return unaryInvoke<TaskSearchGetProgressRequest, TaskSearchGetProgressResponse>(
        req,
        client.getProgress.bind(client),
        (payload) => {
            dispatch({
                type: TasksAction.getProgress,
                payload,
            })
        },
        notifyClient,
    )
}

export const getAllAppsForTaskSearch = (
    dispatch: Dispatch<TasksActions>,
    client: TaskSearchServiceClient | undefined,
    request?: GetAllAppsForTaskSearchRequest,
    notifyClient?: NotifyClient,
): CancelFunc => {
    if (!client || !request) {
        return emptyCancel
    }
    return unaryInvoke<GetAllAppsForTaskSearchRequest, GetAllAppsForTaskSearchResponse>(
        request,
        client.getAllAppsForTaskSearch.bind(client),
        (payload) => {
            switch (payload.status) {
                case Status.OK:
                    dispatch({
                        type: TasksAction.searchForApps,
                        payload: {
                            status: Status.OK,
                            item: payload.item.items,
                            metadata: payload.metadata,
                        },
                    })
                    break
                default:
                    dispatch({
                        type: TasksAction.searchForApps,
                        payload,
                    })
            }
        },
        notifyClient,
    )
}

export const deleteTask = (
    dispatch: Dispatch<TasksActions>,
    client: TaskServiceClient | undefined,
    objectID: string,
): CancelFunc => {
    if (!client) {
        return emptyCancel
    }

    return unaryInvoke<TaskServiceDeleteRequest, TaskServiceDeleteResponse>(
        TaskServiceDeleteRequest.create({
            id: objectID,
        }),
        client.delete.bind(client),
        (payload) => {
            dispatch({
                type: TasksAction.delete,
                payload,
            })
        },
    )
}

export const approve = (
    dispatch: Dispatch<TasksActions>,
    client: TaskActionsServiceClient | undefined,
    taskId: string,
    action: Action,
): void => {
    if (!client) {
        return
    }
    unaryInvoke<TaskActionsServiceApproveRequest, TaskActionsServiceApproveResponse>(
        TaskActionsServiceApproveRequest.create({
            taskId,
            comment: action.comment,
            policyStepId: action.policyStepId,
            expandMask: { paths: ['*'] },
        }),
        client.approve.bind(client),
        (payload) => {
            dispatch({
                type: TasksAction.approve,
                payload: expandTaskPayload(payload),
            })
        },
    )
}

export const markProvisionComplete = (
    dispatch: Dispatch<TasksActions>,
    client: TaskActionsServiceClient | undefined,
    taskId: string,
    action: Action,
): void => {
    if (!client) {
        return
    }
    unaryInvoke<TaskActionsServiceMarkProvisionCompleteRequest, TaskServiceActionResponse>(
        TaskActionsServiceMarkProvisionCompleteRequest.create({
            taskId,
            comment: action.comment,
            policyStepId: action.policyStepId,
            expandMask: { paths: ['*'] },
        }),
        client.markProvisionComplete.bind(client),
        (payload) => {
            dispatch({
                type: TasksAction.provisionComplete,
                payload: expandTaskPayload(payload),
            })
        },
    )
}

export const markProvisionErrored = (
    dispatch: Dispatch<TasksActions>,
    client: TaskActionsServiceClient | undefined,
    taskId: string,
    action: Action,
): void => {
    if (!client) {
        return
    }
    unaryInvoke<TaskActionsServiceMarkProvisionErroredRequest, TaskServiceActionResponse>(
        TaskActionsServiceMarkProvisionErroredRequest.create({
            taskId,
            comment: action.comment,
            policyStepId: action.policyStepId,
            expandMask: { paths: ['*'] },
        }),
        client.markProvisionErrored.bind(client),
        (payload) => {
            dispatch({
                type: TasksAction.provisionErrored,
                payload: expandTaskPayload(payload),
            })
        },
    )
}

export const deny = (
    dispatch: Dispatch<TasksActions>,
    client: TaskActionsServiceClient | undefined,
    taskId: string,
    action: Action,
): void => {
    if (!client) {
        return
    }
    unaryInvoke<TaskActionsServiceDenyRequest, TaskActionsServiceDenyResponse>(
        TaskActionsServiceDenyRequest.create({
            taskId,
            comment: action.comment,
            policyStepId: action.policyStepId,
            expandMask: { paths: ['*'] },
        }),
        client.deny.bind(client),
        (payload) => {
            dispatch({
                type: TasksAction.deny,
                payload: expandTaskPayload(payload),
            })
        },
    )
}

export const close = (
    dispatch: Dispatch<TasksActions>,
    client: TaskActionsServiceClient | undefined,
    taskId: string,
    action: Action,
): void => {
    if (!client) {
        return
    }
    unaryInvoke<TaskActionsServiceCloseRequest, TaskActionsServiceCloseResponse>(
        TaskActionsServiceCloseRequest.create({
            taskId,
            comment: action.comment,
            expandMask: { paths: ['*'] },
        }),
        client.close$.bind(client),
        (payload) => {
            dispatch({
                type: TasksAction.close,
                payload: expandTaskPayload(payload),
            })
        },
    )
}

export const comment = (
    dispatch: Dispatch<TasksActions>,
    client: TaskActionsServiceClient | undefined,
    taskId: string,
    commentStr: string,
): void => {
    if (!client) {
        return
    }
    unaryInvoke<TaskActionsServiceCommentRequest, TaskActionsServiceCommentResponse>(
        TaskActionsServiceCommentRequest.create({ taskId, comment: commentStr, expandMask: { paths: ['*'] } }),
        client.comment.bind(client),
        (payload) => {
            dispatch({
                type: TasksAction.comment,
                payload: expandTaskPayload(payload),
            })
        },
    )
}

export const reassign = (
    dispatch: Dispatch<TasksActions>,
    client: TaskActionsServiceClient | undefined,
    taskId: string,
    action: Action,
): void => {
    if (!client) {
        return
    }
    unaryInvoke<TaskActionsServiceReassignRequest, TaskServiceActionResponse>(
        TaskActionsServiceReassignRequest.create({
            taskId,
            comment: action.comment,
            newStepUserIds: action.reassignedUserIDs,
            policyStepId: action.policyStepId,
            expandMask: { paths: ['*'] },
        }),
        client.reassign.bind(client),
        (payload) => {
            dispatch({
                type: TasksAction.reassign,
                payload: expandTaskPayload(payload),
            })
        },
    )
}

export const restartTask = (
    dispatch: Dispatch<TasksActions>,
    client: TaskActionsServiceClient | undefined,
    taskId: string,
    action: Action,
): CancelFunc | undefined => {
    if (!client) {
        return
    }
    return unaryInvoke<TaskActionsServiceRestartRequest, TaskActionsServiceRestartResponse>(
        TaskActionsServiceRestartRequest.create({
            taskId,
            comment: action.comment,
            expandMask: { paths: ['*'] },
        }),
        client.restart.bind(client),
        (payload) => {
            dispatch({
                type: TasksAction.restart,
                payload: expandTaskPayload(payload),
            })
        },
    )
}

export const escalateToEmergencyAccess = (
    dispatch: Dispatch<TasksActions>,
    client: TaskActionsServiceClient | undefined,
    taskId: string,
    action: Action,
): CancelFunc | undefined => {
    if (!client) {
        return
    }
    return unaryInvoke<TaskActionsServiceEscalateToEmergencyAccessRequest, TaskServiceActionResponse>(
        TaskActionsServiceRestartRequest.create({
            taskId,
            comment: action.comment,
            policyStepId: action.policyStepId,
            expandMask: { paths: ['*'] },
        }),
        client.escalateToEmergencyAccess.bind(client),
        (payload) => {
            dispatch({
                type: TasksAction.escalateToEmergencyAccess,
                payload: expandTaskPayload(payload),
            })
        },
    )
}

export const getSendReminderPreview = (
    dispatch: Dispatch<GetSendReminderPreview>,
    client: TaskServiceClient | undefined,
    taskId: string,
): CancelFunc => {
    if (!client) {
        return emptyCancel
    }

    return unaryInvoke<TaskServiceGetSendReminderPreviewRequest, TaskServiceGetSendReminderPreviewResponse>(
        TaskServiceGetSendReminderPreviewRequest.create({
            taskId,
        }),
        client.getSendReminderPreview.bind(client),
        (payload) => {
            if (payload.status === Status.OK) {
                dispatch({
                    type: TasksAction.getSendReminderPreview,
                    payload: {
                        item: payload.item.taskApprovers || TaskApprovers.create(),
                        status: Status.OK,
                    },
                })
            } else {
                dispatch({
                    type: TasksAction.getSendReminderPreview,
                    payload,
                })
            }
        },
    )
}

export const sendReminder = async (
    dispatch: Dispatch<SendReminder>,
    actionClient: TaskServiceClient | undefined,
    taskId: string,
    copyManagers: boolean,
    message: string,
    copiedUserIds: string[],
): Promise<void> => {
    if (!actionClient) {
        return
    }
    const payload = await actionInvoke<TaskServiceSendReminderRequest, Empty>(
        { taskId, message, copiedUserIds, copyManagers },
        actionClient.sendReminder.bind(actionClient),
    )
    dispatch({ type: TasksAction.sendReminder, payload })
}

export const stringFromTaskAction = (action: TaskActionType): string => {
    switch (action) {
        case TaskActionType.ESCALATE_TO_EMERGENCY_ACCESS:
            return 'escalate to emergency access'
        default:
            return TaskActionType[action].toLowerCase()
    }
}
